import { useState } from 'react';
import { SchreibtAls } from '@/components/ui/SchreibtAls';
import { Plus, X, PackageCheck, Copy, Check, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { PositionRow } from './PositionRow';
import { IsbnInput } from '@/components/bestellung/IsbnInput';
import { FreitextInput } from '@/components/bestellung/FreitextInput';
import { apiFetch, ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { WegFrage, type WegWahl } from './WegFrage';
import { useQueryClient } from '@tanstack/react-query';
import type { Bestellung, DetailPosition } from '@/types/detail';
import type { PositionInput, PositionWire } from '@/types/bestellung';
import { abschlussWeg, bereitKnopfLabel } from '@/lib/zustandLabel';
import { positionenSortiert } from '@/lib/positionenReihenfolge';
import { sendungsstandSatz } from '@/lib/sendungsstand';
import { vergriffenText } from '@/lib/vergriffenText';
import { NachlieferungKopieren } from './NachlieferungKopieren';
import { useZeigerFein } from '@/hooks/useLayout';

type Props = {
  /** data-tour-Name fuer die Einfuehrungstour. */
  anker?: string;
  bestellung: Bestellung;
  /** Endzustands-Rueckfrage aus der Route (B15). */
  bestaetigeAenderung: () => Promise<boolean>;
  /** Oeffnet den Sendungs-Dialog (B56). */
  onBereitSetzen: () => void;
};

export function PositionenSektion({
  anker,
  bestellung,
  bestaetigeAenderung,
  onBereitSetzen
}: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [formOffen, setFormOffen] = useState(false);
  /**
   * Offene Rückfrage „von wem kam es?" (E2).
   *
   * Der Server weigert sich zu raten, wenn mehrere Beschaffungswege
   * offen sind, und schickt die Liste gleich mit (409
   * mehrere_wege_offen). Hier wartet sie, bis der Mensch geantwortet
   * hat — dann geht derselbe Aufruf noch einmal hinaus, diesmal mit
   * Zuordnung.
   */
  const [wegFrage, setWegFrage] = useState<{
    position: DetailPosition;
    ziel: number;
    wege: WegWahl[];
  } | null>(null);

  // Der Textbaustein ist für den Rechnungs-Vortext in BONUS — und BONUS
  // läuft am PC. Am Tablet gäbe es nichts, wohin man ihn einfügen könnte.
  const zeigerFein = useZeigerFein();
  // Aus Shopify: Positionen führt der Shop (E47, lib/shopsperre.php).
  const ausShop = bestellung.shopify_order_id !== null;
  const vergriffenSatz = vergriffenText(bestellung.positionen);
  const [kopiert, setKopiert] = useState(false);

  async function vergriffenKopieren(): Promise<void> {
    if (vergriffenSatz === null) return;
    try {
      await navigator.clipboard.writeText(vergriffenSatz);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 2000);
    } catch {
      toast('error', 'Kopieren ging nicht — die Zwischenablage ist gesperrt.');
    }
  }

  /**
   * Vergriffen zurücknehmen — hinter einer Tipp-Wand.
   *
   * Setzen ist ein Tipp an der Zeile; Zurücknehmen will getippt sein. Die
   * Asymmetrie ist Absicht: Wer zurücknimmt, sagt, dass der Titel doch
   * kommt — dann muss er wieder beschafft werden, und genau das soll
   * niemand aus Versehen auslösen.
   */
  async function vergriffenZuruecknehmen(p: DetailPosition): Promise<void> {
    if (!(await bestaetigeAenderung())) return;
    const ok = await confirm({
      titel: 'Vergriffen zurücknehmen?',
      text: `„${p.titel}“ gilt dann wieder als lieferbar und muss beschafft werden.`,
      bestaetigenLabel: 'Zurücknehmen',
      tippWort: 'RÜCKGÄNGIG'
    });
    if (!ok) return;
    await patchRoh(
      '/bestellungen/positionen/vergriffen.php',
      { position_id: p.id, menge_vergriffen: 0 },
      'Vergriffen zurückgenommen.'
    );
  }

  const positionsInSendungen = new Set<number>();
  for (const s of bestellung.sendungen) {
    for (const sp of s.positionen) positionsInSendungen.add(sp.position_id);
  }

  // Gegen die WIRKSAME Menge (Migration 035): Vergriffenes kommt nie.
  // Mindestens eine Position muss etwas zu liefern haben — ist alles
  // vergriffen, gibt es nichts bereitzulegen.
  const allesEingegangen =
    bestellung.positionen.some((p) => p.menge_wirksam > 0) &&
    bestellung.positionen.every((p) => p.menge_eingegangen >= p.menge_wirksam);

  async function patchRoh<T>(path: string, body: T, erfolg = 'Position gespeichert.'): Promise<void> {
    try {
      await apiFetch(path, { method: 'POST', body: JSON.stringify(body) });
      qc.invalidateQueries({ queryKey: ['bestellung', bestellung.bestellnr] });
      toast('success', erfolg);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'unbekannt';
      // Die Übermenge ist der einzige Fehler hier, den der Mitarbeiter
      // selbst verursacht und selbst beheben kann — sie bekommt deshalb
      // einen deutschen Satz statt eines Fehlercodes. Er tritt auf, wenn
      // jemand anders in der Zwischenzeit denselben Rest gebucht hat.
      if (code === 'invalid_input' && path.includes('/beschaffung/')) {
        toast('error', 'Mehr als offen ist, geht nicht — die Seite lädt gleich neu.');
        qc.invalidateQueries({ queryKey: ['bestellung', bestellung.bestellnr] });
        return;
      }
      // Menge unter das schon Eingetroffene (A-02). Auch das verursacht
      // und behebt der Mitarbeiter selbst, also ein Satz statt eines
      // Codes — mit der Zahl, denn ohne sie wüsste niemand, wo die Grenze
      // liegt. Der Weg dorthin führt über den Eingang, nicht über die
      // Menge; das sagt der Satz mit.
      // Vergriffen über das hinaus, was noch offen ist — inzwischen ist
      // etwas eingetroffen. Was im Haus liegt, ist nicht vergriffen.
      if (code === 'ueber_grenze') {
        const h = Number((err instanceof ApiError ? err.daten.hoechstens : 0) ?? 0);
        toast(
          'error',
          h === 0
            ? 'Inzwischen ist alles eingetroffen — da ist nichts mehr vergriffen.'
            : `Höchstens ${h} können vergriffen sein — der Rest ist schon da.`
        );
        qc.invalidateQueries({ queryKey: ['bestellung', bestellung.bestellnr] });
        return;
      }
      if (code === 'below_eingang') {
        const eingetroffen = Number(
          (err instanceof ApiError ? err.daten.eingang : 0) ?? 0
        );
        toast(
          'error',
          `Es sind schon ${eingetroffen} Stück eingetroffen — weniger geht nicht. ` +
            'Erst den Eingang verringern.'
        );
        return;
      }
      toast('error', `Speichern fehlgeschlagen: ${code}`);
    }
  }

  // Mit vorgeschalteter Endzustands-Rueckfrage (B15).
  async function patch<T>(path: string, body: T, erfolg?: string): Promise<void> {
    if (!(await bestaetigeAenderung())) return;
    await patchRoh(path, body, erfolg);
  }

  /**
   * A12.5 — Rückfrage, bevor an einer Position gerückt wird, die schon
   * in einer Sendung steckt.
   *
   * KEINE SPERRE, EIN ZWISCHENSCHRITT
   *   Es gibt echte Gründe, den Eingang einer versandten Position noch zu
   *   ändern: die Kiste war doch nicht vollständig, jemand hat zu früh
   *   gebucht, ein Exemplar kam zurück. Wer das verbietet, zwingt zur
   *   Notlüge an anderer Stelle. Was fehlte, war nicht das Verbot,
   *   sondern der Hinweis — an der Zeile stand nirgends, dass das Buch
   *   längst draußen ist.
   *
   *   Die Rückfrage nennt deshalb das Datum. „Ist raus" allein hilft
   *   nicht; ob gestern oder im März entscheidet, ob man den Eingang
   *   korrigiert oder erst beim Kunden nachfragt.
   *
   * Für Positionen ohne Sendung ändert sich nichts — dort kommt gar
   * keine Rückfrage.
   */
  async function bestaetigeTrotzSendung(p: DetailPosition, was: string): Promise<boolean> {
    const satz = sendungsstandSatz(p.sendungsstand);
    if (satz === null) return true;
    return confirm({
      titel: 'Position ist schon in einer Sendung',
      text: `„${p.titel}“ ${satz}. ${was}`,
      bestaetigenLabel: 'Trotzdem ändern'
    });
  }

  /**
   * Eingang buchen — mit der einen Rückfrage, die es geben darf.
   *
   * Der Zielwert geht hinaus wie bisher. Antwortet der Server mit 409,
   * sind mehrere Wege offen und er hat sich geweigert zu raten; dann
   * fragt die Oberfläche und ruft mit der Antwort erneut auf. Bei genau
   * einem offenen Weg passiert nichts davon — der Normalfall am Tresen
   * kostet keinen zusätzlichen Tipp (E2).
   */
  async function eingangBuchen(
    p: DetailPosition,
    ziel: number,
    zuordnung?: { beschaffung_id: number } | { ohne_zuordnung: true }
  ): Promise<void> {
    if (zuordnung === undefined && !(await bestaetigeAenderung())) return;
    // Nur beim ersten Anlauf fragen. Kommt der Aufruf ein zweites Mal
    // herein, weil die Weg-Rueckfrage beantwortet wurde, ist diese Frage
    // schon bejaht — zweimal dasselbe zu fragen erzieht zum Wegklicken.
    if (
      zuordnung === undefined &&
      !(await bestaetigeTrotzSendung(p, 'Soll der Eingang trotzdem geändert werden?'))
    ) {
      return;
    }
    try {
      const antwort = await apiFetch<{ lager_zugeordnet?: boolean }>(
        '/bestellungen/positionen/eingang.php',
        {
          method: 'POST',
          body: JSON.stringify({
            position_id: p.id,
            menge_eingegangen: ziel,
            ...(zuordnung ?? {})
          })
        }
      );
      qc.invalidateQueries({ queryKey: ['bestellung', bestellung.bestellnr] });
      // Regel 4a (31.10.2026): Ohne jeden erfassten Weg legt der Server
      // den Eingang auf „Lager". Das sagt die Meldung — sichtbar machen,
      // nicht still tun (Prinzip 3), damit niemand den Eintrag sucht.
      toast(
        'success',
        antwort.lager_zugeordnet === true
          ? 'Eingang gebucht — dem Lager zugeordnet.'
          : 'Position gespeichert.'
      );
    } catch (err) {
      if (err instanceof ApiError && err.code === 'mehrere_wege_offen') {
        const wege = (err.daten.wege ?? []) as WegWahl[];
        setWegFrage({ position: p, ziel, wege });
        return;
      }
      const code = err instanceof ApiError ? err.code : 'unbekannt';
      toast('error', `Speichern fehlgeschlagen: ${code}`);
    }
  }

  // Inline-Erfassung einer neuen Position (SPEC-011). Wiederverwendung der
  // IsbnInput/FreitextInput aus SPEC-004; `_uid` ist client-only und wird
  // vor dem Submit gestrippt.
  async function addPosition(p: PositionInput): Promise<void> {
    if (!(await bestaetigeAenderung())) return;
    const { _uid: _ignored, ...wire } = p;
    const body: PositionWire & { bestellung_id: number } = {
      ...wire,
      bestellung_id: bestellung.id
    };
    try {
      await apiFetch('/bestellungen/positionen/add.php', {
        method: 'POST',
        body: JSON.stringify(body)
      });
      qc.invalidateQueries({ queryKey: ['bestellung', bestellung.bestellnr] });
      toast('success', 'Position hinzugefügt.');
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'unbekannt';
      toast('error', `Hinzufügen fehlgeschlagen: ${code}`);
    }
  }

  // Loeschen ist serverseitig endgueltig — deshalb Rueckfrage statt
  // Sofortvollzug (B6). Der bewusste Zwischenschritt loest zugleich das
  // Nachruecken der Liste unter dem Cursor.
  async function loeschePosition(p: DetailPosition): Promise<void> {
    // Erst die Endzustands-Rueckfrage, dann die Loesch-Rueckfrage — sonst
    // stapeln sich zwei Dialoge in unguenstiger Reihenfolge.
    if (!(await bestaetigeAenderung())) return;
    const ok = await confirm({
      titel: 'Position löschen?',
      text: `„${p.titel}“ wird dauerhaft aus der Bestellung entfernt.`,
      bestaetigenLabel: 'Löschen',
      destruktiv: true
    });
    if (!ok) return;
    await patchRoh('/bestellungen/positionen/delete.php', { position_id: p.id });
  }

  // Eine Beschaffung löscht man wegen einer Fehleingabe, nicht weil eine
  // Bestellung zurückgenommen wurde — beim Grossisten steht sie dann ja
  // trotzdem. Deshalb dieselbe Rückfrage wie bei der Position: der
  // Zwischenschritt zwingt zum Hinsehen, und der Chip ist klein.
  async function loescheBeschaffung(id: number, beschriftung: string): Promise<void> {
    if (!(await bestaetigeAenderung())) return;
    const ok = await confirm({
      titel: 'Beschaffung entfernen?',
      text: `„${beschriftung}“ wird aus dem Bestell-Journal gestrichen. Für Fehleingaben gedacht — beim Lieferanten bleibt die Bestellung bestehen.`,
      bestaetigenLabel: 'Entfernen',
      destruktiv: true
    });
    if (!ok) return;
    await patchRoh(
      '/bestellungen/positionen/beschaffung/delete.php',
      { id },
      'Beschaffung entfernt.'
    );
  }

  return (
    <section className="space-y-2" data-tour={anker}>
      {dialog}
      {/* Kopfzeile in zwei Etagen.
          ----------------------------------------------------------------
          Vorher standen Überschrift und ALLE Knöpfe in einer Zeile mit
          `justify-between`. Solange höchstens ein Hinweis-Knopf da war,
          ging das auf. Sind es beide, wächst der rechte Block über die
          freie Breite hinaus und schiebt sich über die Überschrift
          „Positionen" — im Fenster des Operators zwischen etwa 900 und
          1100 Pixeln (Regression zu B56/B57).

          Die Ursache ist nicht die Breite, sondern die Zeile: ein
          `flex`-Kind ohne `min-width: 0` schrumpft nicht unter seinen
          Inhalt, und dann rechnet der Umbruch mit einer Breite, die es
          nicht gibt. Statt daran zu drehen, bekommen die Hinweise eine
          eigene Etage — sie sind ohnehin Angebote und keine Kopfzeile.
          Damit gibt es KEINE Breite mehr, bei der sich etwas überlagern
          kann, auch nicht auf dem schmalen Tablet. */}
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-serif text-base text-ink">Positionen</h3>
        {/* A9.1: Der Wareneingang wird hier gebucht — auch das ist eine
            Schreibstelle, und auch sie läuft auf einen Namen. */}
        <SchreibtAls text="Gebucht von" className="ml-auto mr-2 hidden sm:block" />
        {ausShop ? (
          <span className="text-xs text-ink-faint">Positionen aus Shopify — dort ändern</span>
        ) : (
          <button
 data-nutzung="position_hinzufuegen"            type="button"
            onClick={() => setFormOffen((o) => !o)}
            className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-md border border-line px-3 py-2 text-sm text-ink-soft hover:border-akzent hover:text-akzent"
            aria-expanded={formOffen}
          >
            {formOffen ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {formOffen ? 'Schließen' : 'Position hinzufügen'}
          </button>
        )}
      </div>

      {/* HIER STAND EIN ZWEITER KNOPF (A7.3)

          „Alles bestellt — Zustand auf Bestellt setzen?" Er entfaellt,
          weil der Zustand jetzt von selbst springt, sobald alle
          Positionen einen Beschaffungsweg haben (E4). Ein Knopf, der nur
          bestaetigt, was ohnehin schon gilt, ist Arbeit ohne Ertrag.

          Der Knopf unten bleibt, und das ist kein Widerspruch: „Bereit"
          ist keine Feststellung, sondern eine Zusage. Dazwischen liegt
          echte Arbeit — Stapel auf den Bereit-Tisch, Rechnung schreiben
          bzw. in Shopify ausfuehren, Versandlabel drucken. Spraenge das
          System selbst dorthin, stuende „abholbereit" im Brett, waehrend
          das Buch noch im Wareneingangskarton liegt. */}
      {allesEingegangen &&
        bestellung.zustand.code !== 'bereit' &&
        !bestellung.zustand.ist_endzustand && (
        <div className="flex flex-col items-start gap-2 sm:flex-row sm:flex-wrap">
          {/* Der Vorschlag gilt nur, solange er nach vorne zeigt. In den
              Endzustaenden — Abgeschlossen, Storniert, Remittiert — stand
              hier ein Knopf, der einen Rueckschritt anbot: die Bestellung
              ist erledigt, und die Oberflaeche schlug vor, sie wieder auf
              "Bereit" zu setzen. "Wartet auf Kunde" ist kein Endzustand,
              dort bleibt der Knopf richtig und sichtbar.

              Die Beschriftung nennt beim Namen, was danach gilt: bei
              Abholung „abholbereit" (Zustand `bereit`), bei Versand
              schlicht „abschliessen" — dort fuehrt der Knopf direkt in
              `abgeschlossen`, und einen Zwischenschritt gibt es seit A46
              nicht mehr. */}
          {abschlussWeg(bestellung) === 'shopify' ? (
            // Glomer mit Versand (E47): Abgeschlossen wird in Shopify
            // („Ausführen", dort auch das easyDPD-Etikett); der Webhook
            // meldet es hierher. Ein eigener Abschluss hier wäre dieselbe
            // Tatsache an zweiter Stelle — und erreichte Shopify nicht.
            <>
              {bestellung.shopify_admin_url ? (
                <a
                  href={bestellung.shopify_admin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-tour="detail-bereit"
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-md border border-line bg-cream px-4 text-sm text-ink hover:border-akzent hover:text-akzent"
                >
                  <ExternalLink className="h-4 w-4" />
                  In Shopify ausführen
                </a>
              ) : null}
              <span className="text-xs text-ink-faint">
                Alles da. Sobald die Bestellung in Shopify ausgeführt ist, schließt sie hier von selbst ab.
              </span>
            </>
          ) : (
            <Button variant="secondary" data-tour="detail-bereit" onClick={onBereitSetzen}>
              <PackageCheck className="h-4 w-4" />
              {bereitKnopfLabel(bestellung.versandart?.code ?? null, zeigerFein)}
            </Button>
          )}
        </div>
      )}

      {/* Nur am PC und nur, wenn etwas vergriffen ist: der Satz gehört in
          den Vortext der Rechnung in BONUS. */}
      {zeigerFein && vergriffenSatz !== null && (
        <div className="flex flex-wrap items-center gap-2">
          <Button data-nutzung="vergriffen_text" variant="secondary" onClick={() => void vergriffenKopieren()}>
            {kopiert ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {kopiert ? 'Kopiert' : 'Vergriffen-Text kopieren'}
          </Button>
          <span className="min-w-0 text-xs text-ink-faint">für den Rechnungs-Vortext in BONUS</span>
        </div>
      )}
      {/* E52: Solange die Nachlieferung offen ist — für die erste Rechnung,
          falls sie nach dem Anlegen der Teilsendung geschrieben wird. */}
      {/* Nicht bei Glomer: Dort kommt die Rechnung aus Shopify, nicht aus BONUS. */}
      {zeigerFein && bestellung.shopify_order_id === null && (
        <NachlieferungKopieren
          titel={bestellung.positionen.filter((p) => p.nachlieferung === 'versand')}
        />
      )}

      {formOffen && (
        <div className="space-y-2 rounded-md border border-line bg-cream-warm p-3">
          <IsbnInput onAdd={(p) => void addPosition(p)} />
          <FreitextInput onAdd={(p) => void addPosition(p)} />
        </div>
      )}

      {wegFrage !== null && (
        <WegFrage
          offen
          titel={wegFrage.position.titel}
          wege={wegFrage.wege}
          onAbbruch={() => setWegFrage(null)}
          onWahl={(id) => {
            const frage = wegFrage;
            setWegFrage(null);
            void eingangBuchen(
              frage.position,
              frage.ziel,
              id === null ? { ohne_zuordnung: true } : { beschaffung_id: id }
            );
          }}
        />
      )}

      <div className="space-y-2">
        {positionenSortiert(bestellung.positionen).map((p) => (
          <PositionRow
            key={p.id}
            position={p}
            canDelete={!positionsInSendungen.has(p.id)}
            ausShop={ausShop}
            onMengeChange={(m) =>
              patch('/bestellungen/positionen/menge.php', { position_id: p.id, menge: m })
            }
            onEingangChange={(e) => void eingangBuchen(p, e)}
            onNotizSave={(n) =>
              patch('/bestellungen/positionen/notiz.php', { position_id: p.id, notiz: n })
            }
            // A56 (#135): der Benachrichtigungswunsch je Titel. Derselbe
            // Endpunkt wie für den Wunsch der Bestellung — es ist
            // dieselbe Angabe an zwei Orten, nur das Ereignis
            // unterscheidet sich.
            onNachrichtWunsch={(w) =>
              patch('/bestellungen/nachricht-wunsch.php', {
                position_id: p.id,
                nachricht_wunsch: w
              })
            }
            // A12.3: Der Preis stand bisher nur da, wenn das VLB einen
            // lieferte. Bei handerfassten Titeln blieb er fuer immer leer.
            onPreisSave={(cent) =>
              patch(
                '/bestellungen/positionen/preis.php',
                { position_id: p.id, preis_cent: cent },
                cent === null ? 'Preis entfernt.' : 'Preis gespeichert.'
              )
            }
            // A1: Der Tippfehler im Titel einer von Hand erfassten
            // Position. Über dieselbe Rückfrage wie jede andere Änderung
            // an einer abgeschlossenen Bestellung (B15).
            onTitelSave={(t) =>
              patch(
                '/bestellungen/positionen/titel.php',
                { position_id: p.id, titel: t },
                'Titel geändert.'
              )
            }
            onAutorSave={(a) =>
              patch(
                '/bestellungen/positionen/autor.php',
                { position_id: p.id, autor: a },
                a === null ? 'Autor entfernt.' : 'Autor gespeichert.'
              )
            }
            // E62: ISBN an einer von Hand erfassten Position. Titel,
            // Autor und Preis bleiben unberührt — das prüft der Server.
            onIsbnSave={(i) =>
              patch(
                '/bestellungen/positionen/isbn.php',
                { position_id: p.id, isbn: i },
                i === null ? 'ISBN entfernt.' : 'ISBN gespeichert.'
              )
            }
            onBeschaffungAdd={(e) =>
              patch(
                '/bestellungen/positionen/beschaffung/add.php',
                {
                  position_id: p.id,
                  beschaffungsweg_id: e.wegId,
                  menge: e.menge,
                  bestellt_am: e.datum,
                  verlag_text: e.verlagText,
                  kanal: e.kanal,
                  meldung: e.meldung,
                  meldung_am: e.meldungAm
                },
                'Beschaffung erfasst.'
              )
            }
            onBeschaffungDelete={(id, beschriftung) =>
              void loescheBeschaffung(id, beschriftung)
            }
            // A60: Der unzugeordnete Eingang bekommt seinen Weg. Über
            // dieselbe Rückfrage wie jede andere Änderung an einer
            // abgeschlossenen Bestellung (B15) und über denselben
            // patch() wie die Nachbarn — die Detailabfrage wird danach
            // neu gezogen, eine eigene Zustandsführung daneben gäbe es
            // nicht zweimal richtig.
            onBeschaffungUmhaengen={(bid, menge) =>
              patch(
                '/bestellungen/positionen/beschaffung/eingang.php',
                { position_id: p.id, beschaffung_id: bid, menge },
                'Eingang zugeordnet.'
              )
            }
            onMeldungSave={(bid, meldung, meldungAm) =>
              patch(
                '/bestellungen/positionen/beschaffung/meldung.php',
                { beschaffung_id: bid, meldung, meldung_am: meldungAm },
                meldung === null ? 'Meldung entfernt.' : 'Meldung gespeichert.'
              )
            }
            onVergriffen={(m) =>
              patch(
                '/bestellungen/positionen/vergriffen.php',
                { position_id: p.id, menge_vergriffen: m },
                'Als vergriffen markiert.'
              )
            }
            onVergriffenZuruecknehmen={() => void vergriffenZuruecknehmen(p)}
            onDelete={() => void loeschePosition(p)}
          />
        ))}
      </div>
    </section>
  );
}
